import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { BellRing, Check, Clipboard, RotateCw, ShieldAlert } from 'lucide-react'
import {
  Button,
  Card,
  LoadMore,
  SelectField,
  StatusBadge,
  TextField,
} from '@shared/design-system/ui'
import { toast } from '@shared/lib/toast/toast-store'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { useSession } from '@shared/lib/auth/session-store'
import { hasAdminPermission } from '@shared/lib/auth/permissions'
import {
  acknowledgeOperationalAlert,
  retryAdminEvent,
  retryGatewayWebhook,
  resolveOperationalAlert,
  useAdminDeadEventsInfinite,
  useAdminGatewayWebhooksInfinite,
  useAdminOperationalAlertsInfinite,
} from '../api/admin.api'
import type { GatewayWebhookItem, OperationalAlertItem } from '../api/admin.api'
import { TechnicalId } from './TechnicalId'
import {
  actionRow,
  alertDetail,
  alertDescription,
  alertFieldList,
  alertFieldRow,
  alertFieldValue,
  alertItem,
  alertItemHeader,
  alertList,
  alertMeaningBox,
  alertMeaningGrid,
  alertMeaningText,
  alertMeta,
  alertSplit,
  alertTechnicalToggle,
  alertTechnicalSummary,
  alertTitle,
  cardHeader,
  compactCell,
  detailGrid,
  detailItem,
  fieldLabel,
  header,
  jsonBox,
  metricCard,
  metricHeader,
  metricIcon,
  metricLabel,
  metricValue,
  muted,
  noteBox,
  page,
  panel,
  panelSubtitle,
  panelTitle,
  statGrid,
  table,
  tableCard,
  tableViewport,
  td,
  th,
  subtitle,
  title,
  titleBlock,
  value,
} from './admin.css'

const statusOptions = [
  { label: 'Abertos e reconhecidos', value: '' },
  { label: 'Abertos', value: 'OPEN' },
  { label: 'Reconhecidos', value: 'ACKED' },
  { label: 'Resolvidos', value: 'RESOLVED' },
]

const severityOptions = [
  { label: 'Todas', value: '' },
  { label: 'Crítico', value: 'CRITICAL' },
  { label: 'Aviso', value: 'WARN' },
]

export function AdminOperationalAlertsScreen() {
  const [status, setStatus] = useState('')
  const [severity, setSeverity] = useState('')
  const [keyFilter, setKeyFilter] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [resolutionNote, setResolutionNote] = useState('')
  const queryClient = useQueryClient()
  const user = useSession((state) => state.user)
  const canManage = hasAdminPermission(user, 'OPS_ALERT_MANAGE')
  const canRetryWebhook = hasAdminPermission(user, 'RECONCILE_RUN')
  const canRetryEvent = hasAdminPermission(user, 'EVENT_RETRY')
  const alerts = useAdminOperationalAlertsInfinite({
    ...(status ? { status } : {}),
    ...(severity ? { severity } : {}),
    ...(keyFilter ? { key: keyFilter } : {}),
  })
  const items = alerts.data?.pages.flatMap((page) => page.items) ?? []
  const selected = items.find((item) => item.id === selectedId) ?? items[0] ?? null
  const summary = alerts.data?.pages[0]?.summary ?? {
    open: 0,
    acknowledged: 0,
    criticalOpen: 0,
    warnOpen: 0,
  }

  const invalidateAlerts = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin-operational-alerts-infinite'] })
  }
  const acknowledgeMutation = useMutation({
    mutationFn: acknowledgeOperationalAlert,
    onSuccess: () => {
      toast.success('Alerta reconhecido.')
      invalidateAlerts()
    },
  })
  const resolveMutation = useMutation({
    mutationFn: (alertId: string) =>
      resolveOperationalAlert(
        alertId,
        resolutionNote.trim() ? { note: resolutionNote.trim() } : {},
      ),
    onSuccess: () => {
      toast.success('Alerta resolvido.')
      setSelectedId(null)
      setResolutionNote('')
      invalidateAlerts()
    },
  })
  const selectedPresentation = selected ? describeAlert(selected) : null
  const recoverableWebhooks = useAdminGatewayWebhooksInfinite({})
  const recoverableWebhookItems =
    recoverableWebhooks.data?.pages.flatMap((page) => page.items) ?? []
  const retryWebhookMutation = useMutation({
    mutationFn: retryGatewayWebhook,
    onSuccess: ({ webhook }) => {
      if (webhook === null) {
        toast.error({
          title: 'Webhook não reprocessado',
          description: 'Este webhook não pode ser reprocessado com segurança.',
        })
        return
      }
      toast.success('Webhook recolocado para processamento.')
      void queryClient.invalidateQueries({ queryKey: ['admin-gateway-webhooks-infinite'] })
    },
    onError: (error) => {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível reprocessar o webhook',
          fallback: 'O webhook permaneceu na fila. Tente novamente.',
        }),
      )
    },
  })
  const deadEvents = useAdminDeadEventsInfinite()
  const deadEventItems = deadEvents.data?.pages.flatMap((page) => page.items) ?? []
  const retryEventMutation = useMutation({
    mutationFn: retryAdminEvent,
    onSuccess: () => {
      toast.success('Evento interno recolocado para processamento.')
      void queryClient.invalidateQueries({ queryKey: ['admin-events-infinite'] })
    },
    onError: (error) => {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível reprocessar o evento',
          fallback: 'O evento permaneceu na fila. Tente novamente.',
        }),
      )
    },
  })

  return (
    <section className={page} data-testid="alerts-screen">
      <div className={header}>
        <div className={titleBlock}>
          <h1 className={title}>Alertas</h1>
          <p className={subtitle}>
            Sinais operacionais do worker, reconciliação, webhooks e rotinas financeiras.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<RotateCw size={16} />}
          onClick={() => {
            void alerts.refetch()
          }}
        >
          Atualizar
        </Button>
      </div>

      <div className={statGrid}>
        <Card padding="md" className={metricCard}>
          <div className={metricHeader}>
            <span className={metricLabel}>Críticos abertos</span>
            <span className={metricIcon}>
              <ShieldAlert size={17} />
            </span>
          </div>
          <span className={metricValue}>{summary.criticalOpen}</span>
        </Card>
        <Card padding="md" className={metricCard}>
          <div className={metricHeader}>
            <span className={metricLabel}>Avisos abertos</span>
            <span className={metricIcon}>
              <BellRing size={17} />
            </span>
          </div>
          <span className={metricValue}>{summary.warnOpen}</span>
        </Card>
        <Card padding="md" className={metricCard}>
          <div className={metricHeader}>
            <span className={metricLabel}>Reconhecidos</span>
            <span className={metricIcon}>
              <Check size={17} />
            </span>
          </div>
          <span className={metricValue}>{summary.acknowledged}</span>
        </Card>
      </div>

      <Card padding="md">
        <div className={detailGrid}>
          <SelectField
            label="Status"
            value={status}
            options={statusOptions}
            onChange={(event) => {
              setStatus(event.currentTarget.value)
              setSelectedId(null)
            }}
          />
          <SelectField
            label="Severidade"
            value={severity}
            options={severityOptions}
            onChange={(event) => {
              setSeverity(event.currentTarget.value)
              setSelectedId(null)
            }}
          />
          <TextField
            label="Chave"
            value={keyFilter}
            placeholder="outbox, webhook, reconcile..."
            onChange={(event) => {
              setKeyFilter(event.currentTarget.value)
              setSelectedId(null)
            }}
          />
        </div>
      </Card>

      <div className={alertSplit}>
        <Card padding="md">
          <div className={alertList}>
            {items.map((item) => {
              const presentation = describeAlert(item)
              return (
                <button
                  type="button"
                  key={item.id}
                  className={alertItem({
                    selected: selected?.id === item.id,
                    severity: item.severity,
                  })}
                  onClick={() => {
                    setSelectedId(item.id)
                    setResolutionNote(item.resolutionNote ?? '')
                  }}
                >
                  <span className={alertItemHeader}>
                    <span className={alertTitle}>{presentation.title}</span>
                    <StatusBadge
                      label={item.severity === 'CRITICAL' ? 'Crítico' : 'Aviso'}
                      tone={item.severity === 'CRITICAL' ? 'closed' : 'informative'}
                    />
                  </span>
                  <span className={alertDescription}>{presentation.short}</span>
                  <span className={alertMeta}>
                    <span>{presentation.area}</span>
                    <span>•</span>
                    <span>{statusLabel(item.status)}</span>
                    <span>•</span>
                    <span>{item.occurrenceCount}x</span>
                    <span>•</span>
                    <span>{formatDateTime(item.lastSeenAt)}</span>
                  </span>
                </button>
              )
            })}
            {!alerts.isLoading && items.length === 0 ? (
              <span className={muted}>Nenhum alerta encontrado.</span>
            ) : null}
          </div>
          <LoadMore
            shown={items.length}
            hasMore={alerts.hasNextPage}
            loading={alerts.isFetchingNextPage}
            onClick={() => {
              void alerts.fetchNextPage()
            }}
          />
        </Card>

        <Card padding="md" className={alertDetail}>
          {selected && selectedPresentation ? (
            <>
              <div className={panel}>
                <h2 className={panelTitle}>{selectedPresentation.title}</h2>
                <p className={panelSubtitle}>{selectedPresentation.description}</p>
              </div>
              <div className={actionRow}>
                <StatusBadge
                  label={statusLabel(selected.status)}
                  tone={statusTone(selected.status)}
                />
                <StatusBadge
                  label={selected.severity === 'CRITICAL' ? 'Crítico' : 'Aviso'}
                  tone={selected.severity === 'CRITICAL' ? 'closed' : 'informative'}
                />
              </div>

              <div className={alertMeaningGrid}>
                <InfoBox label="Impacto" value={selectedPresentation.impact} />
                <InfoBox label="Próxima ação" value={selectedPresentation.action} />
              </div>

              <div className={detailGrid}>
                <Detail label="Área" value={selectedPresentation.area} />
                <Detail label="Ocorrências" value={`${selected.occurrenceCount}`} />
                <Detail label="Primeiro sinal" value={formatDateTime(selected.firstSeenAt)} />
                <Detail label="Último sinal" value={formatDateTime(selected.lastSeenAt)} />
              </div>

              {selectedPresentation.fields.length > 0 ? (
                <div className={panel}>
                  <span className={fieldLabel}>Dados do sinal</span>
                  <div className={alertFieldList}>
                    {selectedPresentation.fields.map((field) => (
                      <div className={alertFieldRow} key={field.label}>
                        <span className={fieldLabel}>{field.label}</span>
                        <span className={alertFieldValue}>{field.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <TechnicalId label="ID" value={selected.id} />
              <details className={alertTechnicalToggle}>
                <summary className={alertTechnicalSummary}>Dados técnicos</summary>
                <pre className={jsonBox}>{prettyFields(selected.fields)}</pre>
              </details>

              {selected.status === 'RESOLVED' ? (
                <div className={panel}>
                  <h3 className={panelTitle}>Resolução</h3>
                  <p className={panelSubtitle}>
                    {selected.resolutionNote || 'Sem nota registrada.'}
                  </p>
                </div>
              ) : null}

              {selected.status !== 'RESOLVED' ? (
                <>
                  <textarea
                    className={noteBox}
                    value={resolutionNote}
                    onChange={(event) => {
                      setResolutionNote(event.currentTarget.value)
                    }}
                    placeholder="Nota de resolução"
                    maxLength={500}
                  />
                  <div className={actionRow}>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Check size={16} />}
                      disabled={
                        !canManage || selected.status !== 'OPEN' || acknowledgeMutation.isPending
                      }
                      onClick={() => {
                        acknowledgeMutation.mutate(selected.id)
                      }}
                    >
                      Reconhecer
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<ShieldAlert size={16} />}
                      disabled={!canManage || resolveMutation.isPending}
                      onClick={() => {
                        resolveMutation.mutate(selected.id)
                      }}
                    >
                      Resolver
                    </Button>
                    <Button
                      variant="text"
                      size="sm"
                      leftIcon={<Clipboard size={16} />}
                      onClick={() => copyAlert(selected, selectedPresentation)}
                    >
                      Copiar
                    </Button>
                  </div>
                </>
              ) : (
                <Button
                  variant="text"
                  size="sm"
                  leftIcon={<Clipboard size={16} />}
                  onClick={() => copyAlert(selected, selectedPresentation)}
                >
                  Copiar
                </Button>
              )}
            </>
          ) : (
            <span className={muted}>Selecione um alerta.</span>
          )}
        </Card>
      </div>

      <Card padding="none" className={tableCard}>
        <div className={cardHeader}>
          <div className={titleBlock}>
            <h2 className={panelTitle}>Webhooks recuperáveis</h2>
            <p className={panelSubtitle}>
              Eventos mortos ou classificados como estrangeiros que podem ser reavaliados com
              reprocesso assistido.
            </p>
          </div>
        </div>
        <div className={tableViewport}>
          <table className={table}>
            <thead>
              <tr>
                <th className={th}>Evento</th>
                <th className={th}>Gateway</th>
                <th className={th}>Status</th>
                <th className={th}>Ownership</th>
                <th className={th}>Tentativas</th>
                <th className={th}>Recebido</th>
                <th className={th}>Erro</th>
                <th className={th}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {recoverableWebhookItems.map((webhook) => (
                <tr key={webhook.id}>
                  <td className={`${td} ${compactCell}`}>
                    <TechnicalId label="Webhook" value={webhook.id} />
                    <br />
                    <span className={muted}>{webhook.gatewayEventId}</span>
                  </td>
                  <td className={td}>{webhook.gateway}</td>
                  <td className={td}>
                    <StatusBadge
                      label={gatewayWebhookStatusLabel(webhook.status)}
                      tone={webhook.status === 'DEAD' ? 'closed' : 'informative'}
                    />
                  </td>
                  <td className={td}>{gatewayWebhookOwnershipLabel(webhook.ownership)}</td>
                  <td className={td}>{webhook.attempts}</td>
                  <td className={td}>{formatDateTime(webhook.receivedAt)}</td>
                  <td className={`${td} ${compactCell}`}>
                    <span className={muted}>{webhook.lastError ?? 'n/a'}</span>
                  </td>
                  <td className={td}>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<RotateCw size={16} />}
                      disabled={!canRetryWebhook || retryWebhookMutation.isPending}
                      onClick={() => {
                        retryWebhookMutation.mutate(webhook.id)
                      }}
                    >
                      Reprocessar
                    </Button>
                  </td>
                </tr>
              ))}
              {!recoverableWebhooks.isLoading && recoverableWebhookItems.length === 0 ? (
                <tr>
                  <td className={td} colSpan={8}>
                    <span className={muted}>Nenhum webhook recuperável.</span>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <LoadMore
          shown={recoverableWebhookItems.length}
          hasMore={recoverableWebhooks.hasNextPage}
          loading={recoverableWebhooks.isFetchingNextPage}
          onClick={() => {
            void recoverableWebhooks.fetchNextPage()
          }}
        />
      </Card>

      <Card padding="none" className={tableCard}>
        <div className={cardHeader}>
          <div className={titleBlock}>
            <h2 className={panelTitle}>Eventos internos mortos</h2>
            <p className={panelSubtitle}>
              Mensagens da outbox que esgotaram as tentativas e exigem reprocesso auditado.
            </p>
          </div>
        </div>
        <div className={tableViewport}>
          <table className={table}>
            <thead>
              <tr>
                <th className={th}>Evento</th>
                <th className={th}>Tipo</th>
                <th className={th}>Produtor</th>
                <th className={th}>Tentativas</th>
                <th className={th}>Ocorrido</th>
                <th className={th}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {deadEventItems.map((event) => (
                <tr key={event.id}>
                  <td className={`${td} ${compactCell}`}>
                    <TechnicalId label="Evento" value={event.id} />
                  </td>
                  <td className={`${td} ${compactCell}`}>{event.type}</td>
                  <td className={td}>{event.producer}</td>
                  <td className={td}>{event.attempts}</td>
                  <td className={td}>{formatDateTime(event.occurredAt)}</td>
                  <td className={td}>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<RotateCw size={16} />}
                      disabled={!canRetryEvent || retryEventMutation.isPending}
                      onClick={() => retryEventMutation.mutate(event.id)}
                    >
                      Reprocessar
                    </Button>
                  </td>
                </tr>
              ))}
              {!deadEvents.isLoading && deadEventItems.length === 0 ? (
                <tr>
                  <td className={td} colSpan={6}>
                    <span className={muted}>Nenhum evento interno morto.</span>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <LoadMore
          shown={deadEventItems.length}
          hasMore={deadEvents.hasNextPage}
          loading={deadEvents.isFetchingNextPage}
          onClick={() => void deadEvents.fetchNextPage()}
        />
      </Card>
    </section>
  )
}

function Detail({ label, value: itemValue }: { label: string; value: string }) {
  return (
    <div className={detailItem}>
      <span className={fieldLabel}>{label}</span>
      <span className={value}>{itemValue}</span>
    </div>
  )
}

function InfoBox({ label, value: itemValue }: { label: string; value: string }) {
  return (
    <div className={alertMeaningBox}>
      <span className={fieldLabel}>{label}</span>
      <p className={alertMeaningText}>{itemValue}</p>
    </div>
  )
}

function statusLabel(status: OperationalAlertItem['status']) {
  if (status === 'OPEN') return 'Aberto'
  if (status === 'ACKED') return 'Reconhecido'
  return 'Resolvido'
}

function statusTone(status: OperationalAlertItem['status']) {
  if (status === 'OPEN') return 'awaiting'
  if (status === 'ACKED') return 'informative'
  return 'success'
}

function gatewayWebhookStatusLabel(status: GatewayWebhookItem['status']) {
  if (status === 'SKIPPED_FOREIGN') return 'Estrangeiro'
  if (status === 'DEAD') return 'Morto'
  return status
}

function gatewayWebhookOwnershipLabel(ownership: GatewayWebhookItem['ownership']) {
  if (ownership === 'OWNED') return 'Vendepay'
  if (ownership === 'FOREIGN') return 'Externo'
  if (ownership === 'UNKNOWN') return 'Indefinido'
  return 'Não classificado'
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}

function prettyFields(fields: Record<string, unknown> | undefined) {
  return JSON.stringify(fields ?? {}, null, 2)
}

type AlertPresentation = {
  title: string
  short: string
  description: string
  impact: string
  action: string
  area: string
  fields: readonly { label: string; value: string }[]
}

function describeAlert(alert: OperationalAlertItem): AlertPresentation {
  const fields = readableFields(alert.fields)
  const count = numberField(alert.fields, 'count')
  const dead = numberField(alert.fields, 'dead')
  const failed = numberField(alert.fields, 'failed')
  const divergent = numberField(alert.fields, 'divergent')
  const checked = numberField(alert.fields, 'checked')

  if (alert.key.startsWith('reconcile-divergent:')) {
    const scope = stringField(alert.fields, 'scope') ?? alert.key.split(':')[1] ?? 'UNKNOWN'
    const scopeLabel = reconciliationScopeLabel(scope)
    return {
      title: `Divergência na reconciliação de ${scopeLabel.toLowerCase()}`,
      short: `${formatCount(divergent, 'divergência encontrada', 'divergências encontradas')} em ${formatCount(
        checked,
        'item conferido',
        'itens conferidos',
      )}.`,
      description: `A conferência automática comparou o que está no gateway com o ledger da Vendepay e encontrou diferença em ${scopeLabel.toLowerCase()}.`,
      impact:
        'Pode existir valor, status ou repasse diferente entre a Vendepay e o provedor de pagamento. Enquanto estiver aberto, trate como risco financeiro operacional.',
      action:
        'Abra a reconciliação da área indicada, compare os casos divergentes e resolva ou reconheça a divergência quando houver explicação operacional.',
      area: `Reconciliação · ${scopeLabel}`,
      fields,
    }
  }

  if (alert.key.startsWith('gateway-webhook.dispute-won.')) {
    const gateway = stringField(alert.fields, 'gateway') ?? 'gateway'
    const gatewayDisputeId = stringField(alert.fields, 'gatewayDisputeId')
    return {
      title: 'Disputa vencida no gateway',
      short: gatewayDisputeId
        ? `A disputa ${gatewayDisputeId} foi vencida e a reversão financeira foi iniciada.`
        : 'Uma disputa foi vencida e a reversão financeira foi iniciada.',
      description:
        'O provedor informou que a disputa foi encerrada como ganha. A Vendepay desfaz automaticamente a dívida principal e a taxa interna de chargeback recuperável.',
      impact:
        'Valores já recuperados são recreditados e recuperações pendentes são canceladas; o caso ainda deve ser conferido se houver divergência operacional.',
      action:
        'Abra a venda e compare ledger, dívidas e a disputa no gateway. Só faça ajuste manual se a reconciliação indicar divergência.',
      area: `Webhooks de pagamento · ${gateway}`,
      fields,
    }
  }

  switch (alert.key) {
    case 'outbox-dead':
      return {
        title: 'Mensagens internas travadas',
        short: `${formatCount(dead, 'mensagem não será reprocessada automaticamente', 'mensagens não serão reprocessadas automaticamente')}.`,
        description: 'Eventos internos chegaram ao limite de tentativas e ficaram em estado morto.',
        impact:
          'Alguma rotina assíncrona pode não ter concluído, como confirmação de pagamento, liquidação comercial, email ou webhook do cliente.',
        action:
          'Verifique a fila outbox, corrija o erro raiz e reprocesse as mensagens afetadas quando for seguro.',
        area: 'Fila interna',
        fields,
      }
    case 'email-log-failed':
      return {
        title: 'Emails transacionais com falha',
        short: `${formatCount(failed, 'email falhou', 'emails falharam')} no envio.`,
        description:
          'O worker tentou enviar emails transacionais e o provedor ou template devolveu falha.',
        impact:
          'Compradores ou vendedores podem não receber avisos de compra, acesso, cobrança ou suporte operacional.',
        action:
          'Confira a configuração do provedor de email e os registros com falha; depois reenvie ou resolva manualmente os casos necessários.',
        area: 'Notificações',
        fields,
      }
    case 'invoice-stuck':
      return {
        title: 'Faturas recorrentes presas',
        short: `${formatCount(count, 'fatura antiga ainda está pendente', 'faturas antigas ainda estão pendentes')}.`,
        description:
          'Cobranças recorrentes ficaram em um estado intermediário por mais tempo que o esperado.',
        impact:
          'Assinaturas podem deixar de cobrar, liberar acesso ou avisar o comprador corretamente.',
        action:
          'Verifique as faturas listadas, o status da assinatura e o estado da cobrança no gateway antes de retentar ou cancelar manualmente.',
        area: 'Recorrência',
        fields,
      }
    case 'invoice-process-failed':
      return {
        title: 'Falha ao processar fatura',
        short: 'Uma cobrança recorrente falhou durante a rotina automática.',
        description: 'O worker encontrou erro ao processar uma fatura específica.',
        impact:
          'A assinatura ligada a essa fatura pode não ter cobrado, expirado, avisado ou liberado acesso como esperado.',
        action:
          'Use a fatura, assinatura e correlação abaixo para encontrar o erro no log, corrigir a causa e retentar a rotina.',
        area: 'Recorrência',
        fields,
      }
    case 'recurring-charge-config-error':
      return {
        title: 'Configuração de recorrência inválida',
        short: 'A rotina de cobrança recorrente não encontrou configuração suficiente para cobrar.',
        description:
          'Há uma assinatura/fatura que depende de credencial, gateway, cliente salvo ou regra de cobrança ausente.',
        impact: 'Renovações podem parar até a configuração ser corrigida.',
        action:
          'Revise a configuração da assinatura, método salvo e gateway; depois retente a cobrança quando os dados estiverem completos.',
        area: 'Recorrência',
        fields,
      }
    case 'gateway-webhook-dead':
      return {
        title: 'Webhooks de pagamento mortos',
        short: 'Eventos de gateway pararam de retentar e precisam de reprocesso manual.',
        description:
          'Webhooks de Stripe, Mercado Pago ou PayPal falharam até o limite e foram marcados como DEAD.',
        impact:
          'Pagamento, reembolso, chargeback ou payout pode ter acontecido no gateway sem refletir no sistema.',
        action:
          'Use a tabela “Webhooks mortos”, leia o erro, corrija a causa e clique em reprocessar o evento afetado.',
        area: 'Webhooks de pagamento',
        fields,
      }
    case 'chargeback-alert-webhook-dead':
      return {
        title: 'Webhooks de alerta de chargeback mortos',
        short: 'Alertas preventivos de chargeback falharam no processamento.',
        description:
          'Eventos do provedor de alerta de fraude/chargeback chegaram, mas não foram aplicados com sucesso.',
        impact:
          'A prevenção pode não casar com a venda a tempo, aumentando risco de chargeback real e taxa de disputa.',
        action:
          'Confira o evento morto, valide os dados de venda/cartão e reprocesse após corrigir a causa do erro.',
        area: 'Chargeback preventivo',
        fields,
      }
    default:
      return {
        title: fallbackTitle(alert),
        short: 'Sinal operacional sem tradução específica no painel.',
        description:
          'O worker registrou um alerta operacional que ainda não tem explicação dedicada na interface.',
        impact: 'Revise os dados técnicos para entender o fluxo afetado antes de resolver.',
        action:
          'Copie o alerta, investigue a chave técnica e adicione uma tradução no painel se ele se tornar recorrente.',
        area: alert.source,
        fields,
      }
  }
}

const FIELD_LABELS: Record<string, string> = {
  count: 'Itens afetados',
  dead: 'Mensagens mortas',
  failed: 'Falhas',
  checked: 'Itens conferidos',
  divergent: 'Divergências',
  scope: 'Área conferida',
  ledgerEntryIds: 'Lançamentos',
  invoiceIds: 'Faturas',
  invoiceId: 'Fatura',
  subscriptionId: 'Assinatura',
  correlationId: 'Correlação',
  cron: 'Rotina',
  processed: 'Processados',
  skipped: 'Ignorados',
  retried: 'Retentativas',
  deadCount: 'Mortos',
  gateway: 'Gateway',
  webhookId: 'Webhook',
  gatewayEventId: 'Evento do gateway',
  gatewayDisputeId: 'Disputa no gateway',
  gatewayPaymentId: 'Pagamento no gateway',
}

function readableFields(
  fields: Record<string, unknown> | undefined,
): readonly { label: string; value: string }[] {
  return Object.entries(fields ?? {}).map(([key, fieldValue]) => ({
    label: FIELD_LABELS[key] ?? humanizeKey(key),
    value: formatFieldValue(fieldValue),
  }))
}

function numberField(fields: Record<string, unknown> | undefined, key: string) {
  const value = fields?.[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function stringField(fields: Record<string, unknown> | undefined, key: string) {
  const value = fields?.[key]
  return typeof value === 'string' ? value : undefined
}

function formatCount(count: number | undefined, singular: string, plural: string) {
  if (count === undefined) return plural
  return `${new Intl.NumberFormat('pt-BR').format(count)} ${count === 1 ? singular : plural}`
}

function formatFieldValue(fieldValue: unknown): string {
  if (Array.isArray(fieldValue)) {
    if (fieldValue.length === 0) return 'Nenhum'
    const preview = fieldValue.slice(0, 4).map((item) => String(item))
    const suffix =
      fieldValue.length > preview.length ? ` (+${fieldValue.length - preview.length})` : ''
    return `${preview.join(', ')}${suffix}`
  }
  if (typeof fieldValue === 'number' && Number.isFinite(fieldValue)) {
    return new Intl.NumberFormat('pt-BR').format(fieldValue)
  }
  if (typeof fieldValue === 'boolean') {
    return fieldValue ? 'Sim' : 'Não'
  }
  if (typeof fieldValue === 'string') {
    return isIsoDate(fieldValue) ? formatDateTime(fieldValue) : fieldValue
  }
  if (fieldValue === null || fieldValue === undefined) {
    return 'n/a'
  }
  return JSON.stringify(fieldValue)
}

function isIsoDate(value: string) {
  return /^\d{4}-\d{2}-\d{2}T/.test(value) && !Number.isNaN(new Date(value).getTime())
}

function humanizeKey(key: string) {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

function fallbackTitle(alert: OperationalAlertItem) {
  if (alert.summary.trim()) return humanizeKey(alert.summary)
  return humanizeKey(alert.key)
}

function reconciliationScopeLabel(scope: string) {
  const normalized = scope.toUpperCase().replace(/[\s-]+/g, '_')
  if (normalized === 'TRANSFER') return 'Repasses'
  if (normalized === 'PAYMENT') return 'Pagamentos'
  if (normalized === 'REFUND') return 'Reembolsos'
  if (normalized === 'CHARGEBACK') return 'Chargebacks'
  if (normalized === 'ORPHAN_PAYMENT') return 'Pagamentos órfãos'
  return humanizeKey(scope)
}

function copyAlert(alert: OperationalAlertItem, presentation: AlertPresentation) {
  void navigator.clipboard?.writeText(
    JSON.stringify(
      {
        id: alert.id,
        title: presentation.title,
        impact: presentation.impact,
        action: presentation.action,
        key: alert.key,
        severity: alert.severity,
        status: alert.status,
        summary: alert.summary,
        fields: alert.fields,
        lastSeenAt: alert.lastSeenAt,
      },
      null,
      2,
    ),
  )
  toast.success('Alerta copiado.')
}
